import React, { useMemo } from "react";
import { isKnownActionKey } from "../domain/globalAction.types";
import { useFormatActionValue } from "../infrastructure/globalActionsMapper";
import { SimpleGrid, SimpleGridColumn } from "../SimpleGrid";
import { defineMessages, useIntl } from "react-intl";
import { useActionLabels } from "../../orchestrator.messages";

interface Row {
  id: string;
  key: string;
  label: string;
  value: string;
}

interface TableProps {
  actions: Record<string, string>;
  onEdit: (key: string) => void;
  onDelete: (key: string) => void;
  width?: string;
}

const messages = defineMessages({
  param: {
    id: "orchestrator.globalActions.form.actionParam",
    defaultMessage: "Param",
  },
  value: {
    id: "orchestrator.globalActions.form.actionValue",
    defaultMessage: "Value",
  },
  actions: {
    id: "orchestrator.globalActions.header.actions",
    defaultMessage: "Actions",
  },
});

export function ActionsTable({ actions, onEdit, onDelete, width = "70%" }: TableProps) {
  const intl = useIntl();
  const actionLabels = useActionLabels();
  const formatActionValue = useFormatActionValue();

  const rows = useMemo<Row[]>(
    () =>
      Object.entries(actions).map(([key, value]) => ({
        id: key,
        key,
        label: isKnownActionKey(key) ? actionLabels[key] : key,
        value: formatActionValue(key, value),
      })),
    [actions, actionLabels, formatActionValue]
  );

  const columns = useMemo<SimpleGridColumn<Row>[]>(
    () => [
      { name: "label", title: intl.formatMessage(messages.param) },
      { name: "value", title: intl.formatMessage(messages.value) },
    ],
    [intl]
  );

  if (rows.length === 0) {
    return null;
  }

  return (
    <SimpleGrid
      rows={rows}
      columns={columns}
      onEdit={row => onEdit(row.key)}
      onDelete={row => onDelete(row.key)}
      actionsColumnTitle={intl.formatMessage(messages.actions)}
      width={width}
    />
  );
}
